import {isPlatformBrowser} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {DcpCartConfigurationForm} from '@app/cart/dcp-cart-configuration.model';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {MultiCurrencyErrorInfo} from '@app/model/multi-currency-cart-error-info.model';
import {DcpConfiguratorFormService} from '@app/product/configurator/services/dcp-configurator-form.service';
import {DcpCartService} from '@app/product/services/dcp-cart.service';
import {AddToCartEventService} from '@app/services/analytics/EDDL/events/checkout/add-to-cart-event.service';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {DcpCustomerRegistrationDecoratorService} from '@app/services/dcp-customer-registration-decorator.service';
import {IFrameMessageService} from '@app/services/iframe/iframe-message.service';
import {ADD_TO_CART_EVENT, DcpPriceData, FeatureTogglesService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActiveCartService} from '@spartacus/cart/base/core';
import {CartAddEntrySuccessEvent, OrderEntry} from '@spartacus/cart/base/root';
import {Product} from '@spartacus/core';
import {Subscription} from 'rxjs';
import {first, skip, take} from 'rxjs/operators';
import {CartDecoratorErrorHandlingService} from '../services/cart-decorator-error-handling.service';
import {DcpEntitlementService} from '../services/dcp-entitlement.service';


@UntilDestroy()
@Component({
  selector: 'dcp-add-to-cart',
  templateUrl: './dcp-add-to-cart.component.html',
  styleUrl: './dcp-add-to-cart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DcpAddToCartComponent implements OnInit, OnDestroy {
  @Input() btnTranslationKey: string = 'addToCart.addToCart';
  @Input() product: Product;
  @Input() cartEntry: OrderEntry;
  @Input() configuratorModel: DcpCartConfigurationForm;
  @Input() formName: string = 'configurator';
  @Input() redirectToAfterAddToCart: string;
  @Input() shouldRedirectWithinIFrame: boolean = false;
  @Input() priceData: DcpPriceData;
  @Input() form: UntypedFormGroup;
  @Input() cypressSelector: string = 'add-to-cart';
  @Input() isDisabled: boolean = false;
  // 'design' is not a real property, so it only works like this
  @Input() isPrimary = true;
  @Output() isProcessing: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() addedToCart: EventEmitter<void> = new EventEmitter<void>();
  @Output() multiCurrencyCartError: EventEmitter<MultiCurrencyErrorInfo> = new EventEmitter<MultiCurrencyErrorInfo>();
  @ViewChild('addToCartButton') addToCartButtonRef: ElementRef;

  globalAccountsFeatureToggle: boolean;
  errorInfo: MultiCurrencyErrorInfo;
  isNeedToRedirect: boolean = false;
  showSpinner: boolean = false;
  isGlobalAccountValid = true;
  isEntitlementCallSuccessful = true;
  globalAccountsFeatureToggleSubscription: Subscription = Subscription.EMPTY;
  productSubscription: Subscription = Subscription.EMPTY;
  isLoadingSubscription: Subscription = Subscription.EMPTY;
  cartSubscription: Subscription = Subscription.EMPTY;
  globalAccountsSubscription: Subscription = Subscription.EMPTY;
  entitlementServiceCallErrorStatusSubscription: Subscription = Subscription.EMPTY;

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    protected cd: ChangeDetectorRef,
    protected activeCartService: ActiveCartService,
    protected iFrameMessageService: IFrameMessageService,
    protected cartDecoratorErrorHandlingService: CartDecoratorErrorHandlingService,
    private dcpCartDecoratorService: DcpCartDecoratorService,
    private configuratorFormService: DcpConfiguratorFormService,
    private router: Router,
    private cartService: DcpCartService,
    private ngZone: NgZone,
    private dcpEntitlementService: DcpEntitlementService,
    private featureTogglesService: FeatureTogglesService,
    private addToCartEventService: AddToCartEventService,
    private customerRegistrationService: DcpCustomerRegistrationDecoratorService,
  ) {
  }

  ngOnInit() {
    this.globalAccountsFeatureToggleSubscription = this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.captureNetNewGlobalAccounts)
      .pipe(untilDestroyed(this)).subscribe(enabled => this.globalAccountsFeatureToggle = enabled);
    this.globalAccountsSubscription = this.configuratorFormService.isGlobalAccountValid$().pipe(untilDestroyed(this)).subscribe((isGlobalAccountValid: boolean) => {
      this.isGlobalAccountValid = isGlobalAccountValid;
    });
    this.entitlementServiceCallErrorStatusSubscription = this.dcpEntitlementService.getEntitlementServiceCallErrorStatus().pipe(untilDestroyed(this)).subscribe((isErrorPresent: boolean) => {
      if (isErrorPresent) {
        this.configuratorModel.data.entitlementSetSource = null;
        this.configuratorModel.data.entitlementSetId = null;
        this.isEntitlementCallSuccessful = false;
      }
    });
  }

  ngOnDestroy() {
    this.productSubscription.unsubscribe();
    this.cartSubscription.unsubscribe();
    this.isLoadingSubscription.unsubscribe();
    this.globalAccountsSubscription.unsubscribe();
    this.entitlementServiceCallErrorStatusSubscription.unsubscribe();
    this.globalAccountsFeatureToggleSubscription.unsubscribe();
  }

  /**
   * @param product the product to add to cart
   */
  addToCart(product: Product): void {
    this.configuratorFormService.setShowErrors(true);

    if (this.isInvalid(product)) {
      return;
    }
    this.setProcessingState(true);
    this.updateGlobalAccountsConfig();
    this.cartSubscription.unsubscribe();
    this.customerRegistrationService.getCurrentUser().pipe(untilDestroyed(this)).subscribe(() => {
      this.cartSubscription = this.dcpCartDecoratorService.addToCart(product, this.configuratorModel)
        .pipe(untilDestroyed(this))
        .subscribe((cart) => {
            this.activeCartService.getActive().pipe(untilDestroyed(this), skip(1), first()).subscribe((activeCart) => {
              this.addToCartEventService.triggerAddToCartEvent(activeCart, cart as CartAddEntrySuccessEvent);
            });
            this.addToCartEventService.triggerEddlAddToCartEvent(cart as CartAddEntrySuccessEvent);
            if (isPlatformBrowser(this.platformId) && this.iFrameMessageService.isIframe() && !this.shouldRedirectWithinIFrame) {
              this.iFrameMessageService.postMessage({eventId: ADD_TO_CART_EVENT});
            } else {
              this.cartService.reloadActiveCart();
              this.addedToCart.emit();
            }
            this.subscribeToLoadingState();
            this.setProcessingState(false);
          },
          (err) => {
            if (err.error?.errors?.find(error => error.type === 'MultiCurrencyCartError')) {
              this.errorInfo = {
                addedItem: this.product.name,
                addedItemAID: this.product.supplierAlternativeAID,
                addedItemCurrency: this.priceData.price.currency,
                currentCartCurrency: null,
              };

              // Created fallback scenario since changes made in configurator.component.ts
              // in `ngOnInit` & `doesActiveRouteMatchProductInEntry` can cause this.cartEntry to be undefined
              // DCP-6907
              if (this.cartEntry) {
                this.errorInfo.currentCartCurrency = this.cartEntry.totalPrice.currencyIso;
                this.emitMultiCurrencyCartError();
              } else {
                this.activeCartService.getActive().pipe(take(1), untilDestroyed(this)).subscribe((cart) => {
                  this.errorInfo.currentCartCurrency = cart.totalPrice.currencyIso;
                  this.emitMultiCurrencyCartError();
                });
              }
            } else {
              this.cartDecoratorErrorHandlingService.handle(err, null);
            }
            this.setProcessingState(false);
          });
    });
  }

  private isInvalid(product): boolean {
    if (!product) {
      return true;
    }
    return this.form?.invalid || !this.isGlobalAccountValid;
  }

  private emitMultiCurrencyCartError(): void {
    this.multiCurrencyCartError.emit(this.errorInfo);
    this.dcpCartDecoratorService.currency = null;
  }

  private setProcessingState(state: boolean): void {
    this.showSpinner = state;
    this.isProcessing.emit(state);
    this.cd.detectChanges();
  }

  /**
   * shows loading spinner as long as the cart is reloading.
   */
  subscribeToLoadingState() {
    if (this.isLoadingSubscription.closed) {
      this.isLoadingSubscription = this.activeCartService.getLoading().pipe(untilDestroyed(this)).subscribe(isLoading => {
        this.showSpinner = isLoading;
        this.cd.detectChanges();

        if (this.redirectToAfterAddToCart && (!this.iFrameMessageService.isIframe() || this.shouldRedirectWithinIFrame)) {
          this.redirect(isLoading, this.redirectToAfterAddToCart);
        }
      });
    }
  }

  redirect(isLoading: boolean, redirectTo: string) {
    const isLoadingFinished: boolean = !isLoading;
    this.isNeedToRedirect = isLoading || this.isNeedToRedirect;
    if (isLoadingFinished && this.isNeedToRedirect) {
      this.ngZone.run(() => this.router.navigate([redirectTo]));
    }
  }

  /**
   * If global accounts feature toggle is OFF - setting entitlementSetId = entitlementSetSource = null.
   * Otherwise, in case there are no global accounts available for the user to choose - setting entitlementSetId to 'Use Default'
   * and entitlementSetSource to 'System'.
   *
   * @private
   */
  private updateGlobalAccountsConfig(): void {
    if (!this.globalAccountsFeatureToggle) {
      this.configuratorModel.data.entitlementSetId = null;
      this.configuratorModel.data.entitlementSetSource = null;
      return;
    }
    if (!this.configuratorModel.data.entitlementSetId && this.isEntitlementCallSuccessful) {
      this.configuratorModel.data.entitlementSetId = 'Use Default';
    }
    if (!this.configuratorModel.data.entitlementSetSource && this.isEntitlementCallSuccessful) {
      this.configuratorModel.data.entitlementSetSource = 'System';
    }
  }
}
